import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import useScrollSpy from 'react-use-scrollspy';
import { dateFormats } from '@goodfynd/strings';
import { Alert, Button, FlexView, Icon, Link, Text } from '@goodfynd/ui';
import { dateUtil } from '@goodfynd/utils';
import Tab from '@mui/material/Tab';
import { useRouter } from 'next/router';

import config from '../../../config';
import { useVendorApi } from '../../../hooks/api';
import type { VendorProductsResponse } from '../../../services/api/types';
import { useMobileSize } from '../../../utils/base';
import ProductModal from '../../ProductModal';

import { StyledCategoryTabs, StyledProductContainer } from './styles';
import type { VendorProductsProps } from './types';
import VendorProductsCategory from './VendorProductsCategory';
import VendorProductsLoading from './VendorProductsLoading';

function VendorProducts({
  css,
  isPreview,
  menuId = '',
  onProductClick,
  orgMenuId = '',
  onProductsLoaded,
  preview,
  vendorNameId,
}: VendorProductsProps) {
  const api = useVendorApi();
  const {
    query: { params },
  } = useRouter();

  useEffect(() => {
    if (!menuId) {
      onProductsLoaded?.(false);
    }
  });

  /* Fetch products */
  const { data, error, isLoading } = useQuery<
    VendorProductsResponse,
    ResponseError
  >(
    [config.queryKeys.vendorProducts, menuId, orgMenuId],
    () => {
      return menuId ? api.getMenu(menuId) : api.getOrgMenu(orgMenuId);
    },
    {
      enabled: !!(menuId || orgMenuId),
      onSettled(data) {
        if (data?.categories) {
          onProductsLoaded?.(!!data.categories[0]?.products?.[0]);
        }
      },
      onSuccess(data) {
        setMenu(data);
      },
    }
  );

  const [loading, toggleLoading] = useState(false);
  const [menu, setMenu] = useState(data);

  const refreshMenu = useCallback(async () => {
    try {
      toggleLoading(true);
      const refreshedData = await (menuId
        ? api.refreshMenu(menuId)
        : api.refreshOrgMenu(orgMenuId));
      setMenu(refreshedData);
    } catch (error) {
      throw error;
    } finally {
      toggleLoading(false);
    }
  }, [api, menuId, orgMenuId]);

  const categories = useMemo(() => menu?.categories || [], [menu?.categories]);

  const refs = useMemo(
    () => categories.map(() => React.createRef<HTMLElement>()),
    [categories]
  );

  const [viewProduct, toggleProductModal] = useState(false);

  useEffect(() => {
    if (params?.[1]) {
      toggleProductModal(true);
    }
  }, [params]);

  const currentCategory = useScrollSpy({
    sectionElementRefs: refs,
    offsetPx: -100,
  });

  const sortedCategories = useMemo(
    () =>
      categories
        ?.filter(
          (category) =>
            !category.isDeleted &&
            category.products.some((product) => !product.isDeleted)
        )
        .sort((catA, catB) => {
          if (!catA.order || !catB.order) {
            return 99;
          }

          return catA.order - catB.order;
        }),
    [categories]
  );

  const isMobile = useMobileSize();

  const errorMessage = useMemo(() => {
    if (error?.status === 404) {
      return 'Menu not yet configured.';
    }
    return error?.message;
  }, [error]);

  if (isLoading || loading) {
    return <VendorProductsLoading />;
  }

  return (
    <StyledProductContainer css={css}>
      {error && (
        <Alert
          includeIcon={false}
          status="error"
          style={{
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <FlexView
            align="center"
            direction="column"
            justify="center"
            width="100%"
          >
            <Text align="center" inherit>
              {errorMessage}
            </Text>

            {isPreview && (
              <Button
                busy={loading}
                color="neutral"
                size="small"
                href={`${config.env.VENDOR_APP_URL}/dashboard/${
                  vendorNameId ?? '~'
                }/products`}
              >
                Configure Catalog
              </Button>
            )}
          </FlexView>
        </Alert>
      )}

      {!!menu && (
        <>
          {preview && (
            <Alert
              includeIcon={false}
              status="success"
              style={{
                borderRadius: 0,
                height: 'auto',
                marginBottom: 24,
                padding: '12px 16px',
                textAlign: 'center',
                width: '100%',
              }}
            >
              <Text align="center">
                This is a preview of your <b>{menu.name}</b> menu.
                {!!menu.vendorNameId && (
                  <span>
                    &nbsp;Your actual public profile can be &nbsp;
                    <Link
                      display="underline"
                      href={`${config.env.APP_HOST}/vendors/${menu.vendorNameId}`}
                      inherit
                      target="_blank"
                    >
                      viewed here
                    </Link>
                    .
                  </span>
                )}
              </Text>

              <Button
                busy={loading}
                // color="neutral"

                onClick={refreshMenu}
                rank="secondary"
                size="small"
                style={{
                  marginTop: 16,
                  padding: '4px 8px',
                }}
              >
                <Icon
                  name="rotate_left"
                  size={16}
                  label={
                    <div>
                      Last Updated:&nbsp;
                      {menu
                        ? dateUtil.format(
                            menu.timeUpdated,
                            dateFormats.monthDayYearTime
                          )
                        : 'Unknown'}
                    </div>
                  }
                />
              </Button>
            </Alert>
          )}

          <StyledCategoryTabs
            position="sticky"
            scrollButtons={false}
            value={currentCategory}
            variant={isMobile ? 'scrollable' : 'standard'}
          >
            {sortedCategories.map((category, index) => (
              <Tab
                key={category.id}
                label={category.name}
                onClick={() =>
                  refs[index].current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              />
            ))}
          </StyledCategoryTabs>

          {sortedCategories.map((category, index) => (
            <VendorProductsCategory
              category={category}
              containerRef={refs[index]}
              key={category.id}
              onProductClick={onProductClick}
              vendorNameId={vendorNameId}
            />
          ))}

          {viewProduct && (
            <ProductModal
              close={() => toggleProductModal(false)}
              open={viewProduct}
            />
          )}
        </>
      )}
    </StyledProductContainer>
  );
}

export default VendorProducts;
